import '../styles/LessonCard.css';
import '../styles/LessonStatus.css';

import { CourseLessonVm, lessonTypeLabel } from '../models/LessonVm';
import React, { useEffect, useState } from 'react';

import { ApiPortfolio } from '../models/ApiPortfolio';
import { ApiPortfolioState } from '../models/ApiPortfolioState';
import AttemptCard from './AttemptCard';
import AuthService from '../../shared/services/AuthService';
import Box from '@mui/material/Box';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import { Disable } from 'react-disable';
import Grid from '@mui/material/Unstable_Grid2';
import { LessonAttempt } from '../models/LessonAttempt';
import LessonService from '../services/LessonService';
import Typography from '@mui/material/Typography';
import { cloneDeep } from 'lodash';
import useModulesCompletionTime from '../hooks/useModulesCompletionTime';
import { useStatuscode } from '../hooks/useStatuscode';
import useUserModulePortfolio from '../hooks/useUserModulePortfolio';

interface CourseLessonCardProps {
  lesson: CourseLessonVm;
  filteredByStatus: boolean;
  onLaunchCourseLesson: (
    lesson: CourseLessonVm,
    oldAttempt: LessonAttempt
  ) => void;
}

const CourseLessonCard = (props: CourseLessonCardProps) => {
  const {
    type,
    moduleProgressStatus,
    latestPortfolioAttempt,
    lessonkey,
    newVersionAvailable,
    lessonVersion
  } = props.lesson;

  const [assessmentLesson, setAssessmentLesson] = useState<CourseLessonVm>();
  const [previousAttempts, setPrevioustAttempts] = useState<LessonAttempt[]>(
    []
  );

  const { loadPortfolios, loadingPortfolios } = useUserModulePortfolio();
  const { loadEstimate, estimationTime, loadingEstimation } =
    useModulesCompletionTime();
  const { getStatusCode } = useStatuscode();

  useEffect(() => {
    if (latestPortfolioAttempt && type === 'assessment') {
      const userkey = AuthService.Userkey;
      const attempts: LessonAttempt[] = [];
      const newAttempt: LessonAttempt = {
        status: 'N',
        userkey,
        modulekey: lessonkey,
        layer: latestPortfolioAttempt.layer + 1,
        moduleversion: lessonVersion,
        created: undefined,
        modified: undefined,
        completed: undefined,
        expires: undefined
      };
      const portfolioAttempts = async () => {
        const assessmentAttempts: ApiPortfolio[] = await loadPortfolios({
          modulekey: lessonkey,
          userkey
        });
        assessmentAttempts.forEach((p, index) => {
          // 0th attempt is already included as `latestPortfolioAttempt`.
          // B2-1561 Exceptional rule for `assessment` lessons:
          // All previously finalised attempts should be shown for assessment.
          if (index > 0 && p.state === ApiPortfolioState.AssessmentLock) {
            const status = getStatusCode(p);
            attempts.push({ ...p, status });
          }
        });
        // Another exceptional rule:
        // If `newVersionAvailable` is true, then add a empty attempt as `Not started`.
        if (
          newVersionAvailable &&
          latestPortfolioAttempt.state === ApiPortfolioState.AssessmentLock
        ) {
          const copyLesson = cloneDeep(props.lesson);
          attempts.unshift(copyLesson.latestPortfolioAttempt);
          copyLesson.latestPortfolioAttempt = newAttempt;
          setAssessmentLesson(copyLesson);
        } else {
          setAssessmentLesson(props.lesson);
        }
        setPrevioustAttempts(attempts);
      };
      portfolioAttempts();
    }
  }, [props.lesson]);

  useEffect(() => {
    if (lessonkey && lessonVersion) {
      loadEstimate({
        modulekey: lessonkey,
        version: lessonVersion
      });
    }
  }, [lessonkey]);

  const handleCardClick = (
    lesson: CourseLessonVm,
    oldAttempt: LessonAttempt
  ) => {
    lesson.timeToComplete = estimationTime;
    props.onLaunchCourseLesson(lesson, oldAttempt);
  };

  return (
    <Box m="8px 0">
      <Grid container justifyContent={'space-between'}>
        <Grid>
          <Typography
            variant="body2"
            style={{ fontSize: 'small', display: 'flex' }}
          >
            {lessonTypeLabel[type]}
            {loadingEstimation || loadingPortfolios ? (
              <CustomLoadingProgress size={10} style={{ paddingLeft: '5px' }} />
            ) : estimationTime ? (
              LessonService.getModuleEstimationTime(estimationTime)
            ) : (
              ''
            )}
          </Typography>
        </Grid>
        {newVersionAvailable && (
          <Grid style={{ fontSize: '10px' }}>Lesson content updated</Grid>
        )}
      </Grid>
      <Disable disabled={moduleProgressStatus === 'L'}>
        {previousAttempts.length ? (
          <>
            <AttemptCard
              lesson={assessmentLesson || props.lesson}
              onLaunchCourseLesson={handleCardClick}
            />
            {previousAttempts?.map((attempt) => (
              <AttemptCard
                key={attempt.layer}
                lesson={assessmentLesson || props.lesson}
                oldAttempt={attempt}
                onLaunchCourseLesson={handleCardClick}
              />
            ))}
          </>
        ) : (
          <AttemptCard
            lesson={props.lesson}
            onLaunchCourseLesson={handleCardClick}
          />
        )}
      </Disable>
    </Box>
  );
};

export default CourseLessonCard;
